import type { PaymentMethodSummary } from '@tandemone/types'
import { CreditCard } from 'lucide-react'

/* stripe's own casing. anything unlisted falls back to the raw value. */
const CARD_BRANDS: Record<string, string> = {
  amex: 'American Express',
  cartes_bancaires: 'Cartes Bancaires',
  diners: 'Diners Club',
  discover: 'Discover',
  eftpos_au: 'Eftpos Australia',
  jcb: 'JCB',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa'
}

function titleCase(value: string): string {
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function methodLabel(method: PaymentMethodSummary): string {
  if (method.type !== 'card') return titleCase(method.type)
  if (!method.brand) return 'Card'
  return CARD_BRANDS[method.brand] ?? titleCase(method.brand)
}

function expiry(method: PaymentMethodSummary): string | null {
  if (!method.expMonth || !method.expYear) return null
  return `${String(method.expMonth).padStart(2, '0')}/${String(method.expYear).slice(-2)}`
}

function addressLines(method: PaymentMethodSummary): string[] {
  const city = [method.addressCity, method.addressState]
    .filter(Boolean)
    .join(', ')
  return [
    method.addressLine1,
    method.addressLine2,
    [city, method.addressPostalCode].filter(Boolean).join(' ').trim(),
    method.addressCountry
  ].filter((line): line is string => Boolean(line))
}

function Field({ label, children }: { label: string; children: string }) {
  return (
    <div className='flex flex-col gap-0.5'>
      <span className='text-[11px] font-medium tracking-[0.04em] text-ink-soft uppercase'>
        {label}
      </span>
      <span className='text-[13px] text-ink'>{children}</span>
    </div>
  )
}

/* read-only: stripe owns the card, so it is edited in the portal. */
export function PaymentMethodCard({
  method
}: {
  method: PaymentMethodSummary
}) {
  const exp = expiry(method)
  const address = addressLines(method)

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center gap-3'>
        <span className='flex size-9 items-center justify-center rounded-lg border border-border bg-card'>
          <CreditCard className='size-4 text-ink-soft' />
        </span>
        <div className='flex flex-col'>
          <span className='text-[14px] font-medium text-ink'>
            {methodLabel(method)}
            {method.last4 && (
              <span className='ml-1.5 tabular-nums'>•••• {method.last4}</span>
            )}
          </span>
          {exp && (
            <span className='text-[12px] text-ink-soft tabular-nums'>
              Expires {exp}
            </span>
          )}
        </div>
      </div>

      {(method.name || method.email || address.length > 0) && (
        <div className='grid gap-4 sm:grid-cols-3'>
          {method.name && <Field label='Name'>{method.name}</Field>}
          {method.email && <Field label='Email'>{method.email}</Field>}
          {address.length > 0 && (
            <Field label='Billing address'>{address.join(', ')}</Field>
          )}
        </div>
      )}
    </div>
  )
}
